import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import * as offlineDB from "@/utils/offlineDB";
import { queueOp, queueAndFlush, getQueuedDeletedIds } from "@/lib/syncQueue";

const emptyLumber = () => [
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
  { length: "8", quantity: "", description: "Poplar", thickness: "3.5", width: "6" },
];

const defaultComponents = () => [
  { name: "Boards", quantity: "", length: "", height: "", thickness: "", notched: null, notch_length: "", pcs_per_min: "", board_footage: "", cost: "", lumber: emptyLumber(), skids: "", extras: "" },
  { name: "Stringers", quantity: "", length: "", height: "", thickness: "", notched: false, notch_length: "", pcs_per_min: "", board_footage: "", cost: "", lumber: emptyLumber(), skids: "", extras: "" },
  { name: "Nails", quantity: "", length: "", height: "", thickness: "", notched: null, notch_length: "", pcs_per_min: "", board_footage: "", cost: "", lumber: emptyLumber(), skids: "", extras: "" },
];

export function usePalletTypes(user, tutorialModeRef) {
  const [palletTypes, setPalletTypesRaw] = useState([]);
  // Pallet types ordered by first character: digits (123) before letters (ABC)
  const setPalletTypes = useCallback((v) => setPalletTypesRaw(prev => {
    const next = typeof v === 'function' ? v(prev) : v;
    return [...next].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      return an < bn ? -1 : an > bn ? 1 : 0;
    });
  }), []);
  const [selectedPalletType, setSelectedPalletType] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPallet, setEditingPallet] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [formData, setFormData] = useState({ name: "", description: "", notes: "", images: null, components: defaultComponents() });

  const fetchPalletTypes = useCallback(async () => {
    if (tutorialModeRef?.current) return;
    try {
      if (navigator.onLine) {
        const response = await axios.get(`${API}/pallet-types`);
        // Never resurrect pallets deleted offline that haven't synced yet
        const deletedIds = getQueuedDeletedIds("pallet_types");
        let data = deletedIds.length > 0 ? response.data.filter(p => !deletedIds.includes(p.id)) : response.data;
        // Never clobber unsynced local edits with stale server data: a component
        // deleted on weak signal was resurrecting on refetch (and bulkSave was
        // wiping its unsynced flag, so the edit never reached the server)
        let unsynced = [];
        try { unsynced = await offlineDB.getUnsyncedPalletTypes(); } catch {}
        const unsyncedIds = new Set(unsynced.map(p => p.id));
        if (unsynced.length > 0) {
          const localById = new Map(unsynced.map(p => [p.id, p]));
          const serverIds = new Set(data.map(p => p.id));
          data = data.map(p => localById.get(p.id) || p);
          for (const p of unsynced) if (!serverIds.has(p.id)) data.push(p);
        }
        setPalletTypes(data);
        await offlineDB.bulkSavePalletTypes(data.filter(p => !unsyncedIds.has(p.id)));
        try { localStorage.setItem('pt_cache_owner', user?.email || ''); } catch {}
        // Push pending edits now that we're online (weak-signal timeouts never fire
        // the 'online' event, so retry here — with a cooldown to avoid loops)
        if (unsynced.length > 0 && !syncLockRef.current && Date.now() - lastAutoSyncRef.current > 15000) {
          lastAutoSyncRef.current = Date.now();
          syncWithServer();
        }
      } else {
        const localData = await offlineDB.getAllPalletTypes();
        setPalletTypes(localData);
      }
    } catch (error) {
      if (!error.response || error.response.status >= 500) {
        const localData = await offlineDB.getAllPalletTypes();
        if (localData.length > 0) setPalletTypes(localData);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialModeRef, user]);

  useEffect(() => {
    if (user && user !== false) {
      // Show locally stored pallet types instantly, then refresh from the server
      try {
        if (localStorage.getItem('pt_cache_owner') === user.email) {
          offlineDB.getAllPalletTypes().then(local => {
            if (local.length > 0) setPalletTypes(prev => (prev.length > 0 ? prev : local));
          }).catch(() => {});
        }
      } catch {}
      fetchPalletTypes();
    }
  }, [user, fetchPalletTypes]);

  // Offline init
  useEffect(() => {
    const initOffline = async () => {
      await offlineDB.initDB();
      const unsynced = await offlineDB.getUnsyncedPalletTypes();
      setPendingSync(unsynced.length);
    };
    initOffline();
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (!tutorialModeRef?.current) {
        syncWithServer();
        fetchPalletTypes();
      }
    };
    const handleOffline = () => { setIsOnline(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncLockRef = useRef(false);
  const lastAutoSyncRef = useRef(0);
  const syncWithServer = async () => {
    if (tutorialModeRef?.current) return;
    if (syncLockRef.current) return; // prevent concurrent syncs creating duplicates
    syncLockRef.current = true;
    try {
      const unsynced = await offlineDB.getUnsyncedPalletTypes();
      for (const pallet of unsynced) {
        const body = { name: pallet.name, description: pallet.description, notes: pallet.notes, image: pallet.image || null, images: pallet.images || null, components: pallet.components };
        try {
          await axios.get(`${API}/pallet-types/${pallet.id}`);
          await axios.put(`${API}/pallet-types/${pallet.id}`, body, { timeout: 20000 });
        } catch (err) {
          if (err.response?.status === 404) {
            await axios.post(`${API}/pallet-types`, { ...body, id: pallet.id }, { timeout: 20000 });
          } else if (!err.response || err.response.status >= 500) {
            continue; // network/server hiccup — keep unsynced, retry later
          }
        }
        await offlineDB.markAsSynced(pallet.id);
      }
      setPendingSync(0);
      fetchPalletTypes();
    } catch (error) { console.error('Sync failed:', error); }
    finally { syncLockRef.current = false; }
  };

  const resetForm = () => setFormData({ name: "", description: "", notes: "", image: null, images: null, components: defaultComponents() });

  const handleCreateOrUpdate = async () => {
    if (tutorialModeRef?.current) return;
    if (!formData.name.trim()) { toast.error("Please enter a pallet name"); return; }
    const validComponents = formData.components.filter(c => c.name.trim() && c.quantity && parseFloat(c.quantity) > 0).map(c => ({
      name: c.name, quantity: parseFloat(c.quantity),
      length: c.length ? parseFloat(c.length) : null, height: c.height ? parseFloat(c.height) : null,
      thickness: c.thickness ? parseFloat(c.thickness) : null, notched: c.notched,
      notch_length: c.notch_length ? parseFloat(c.notch_length) : null,
      pcs_per_min: c.pcs_per_min ? parseFloat(c.pcs_per_min) : null, board_footage: c.board_footage ? parseFloat(c.board_footage) : null,
      cost: c.cost ? parseFloat(c.cost) : null,
      lumber: c.lumber?.filter(l => l.quantity || l.description).map(l => ({ length: l.length ? parseFloat(l.length) : null, quantity: l.quantity ? parseFloat(l.quantity) : null, description: l.description || null, thickness: l.thickness ? parseFloat(l.thickness) : null, width: l.width || "6" })) || null,
      skids: c.skids ? parseFloat(c.skids) : null,
      extras: c.extras || null,
    }));
    if (validComponents.length === 0) { toast.error("Please add at least one component"); return; }

    setLoading(true);
    const payload = { name: formData.name, description: formData.description, notes: formData.notes, image: formData.image || null, images: formData.images || null, components: validComponents };
    const palletData = { ...payload, id: editingPallet ? editingPallet.id : crypto.randomUUID(), created_at: editingPallet ? editingPallet.created_at : new Date().toISOString(), updated_at: new Date().toISOString() };
    const isEdit = !!editingPallet;
    // Optimistic: appears/updates INSTANTLY, form closes — no waiting on the network
    setPalletTypes(prev => isEdit ? prev.map(p => p.id === palletData.id ? { ...p, ...palletData } : p) : [...prev, palletData]);
    setSelectedPalletType(prev => (prev?.id === palletData.id ? { ...prev, ...palletData } : prev));
    setIsFormOpen(false); setEditingPallet(null); resetForm(); setLoading(false);
    toast.success(isEdit ? "Pallet type updated!" : "Pallet type created!");
    // Write-ahead: saved on this device FIRST, synced in background
    try { await offlineDB.savePalletType(palletData); } catch {}
    try {
      if (isEdit) await axios.put(`${API}/pallet-types/${palletData.id}`, payload, { timeout: 20000 });
      else await axios.post(`${API}/pallet-types`, { ...payload, id: palletData.id }, { timeout: 20000 });
      await offlineDB.savePalletTypeSynced(palletData);
      fetchPalletTypes();
    } catch (error) {
      if (error.response && error.response.status < 500) {
        toast.error(error.response?.data?.detail || "Failed to save pallet type — will retry");
      }
      const unsynced = await offlineDB.getUnsyncedPalletTypes().catch(() => []);
      setPendingSync(unsynced.length);
    }
  };

  const handleDelete = async () => {
    if (tutorialModeRef?.current || !deleteConfirmId) return;
    setLoading(true);
    try {
      await offlineDB.deletePalletType(deleteConfirmId);
      // Write-ahead: server deletion recorded on device FIRST — can never be lost
      // or resurrected by a refetch, even if the app closes mid-request
      queueAndFlush({ method: "delete", path: `/pallet-types/${deleteConfirmId}`, entity: "pallet_types", entityId: deleteConfirmId });
      toast.success("Pallet type deleted");
      setPalletTypes(prev => prev.filter(p => p.id !== deleteConfirmId));
      if (selectedPalletType?.id === deleteConfirmId) { setSelectedPalletType(null); }
      setDeleteConfirmId(null);
    } catch { toast.error("Failed to delete"); }
    finally { setLoading(false); }
  };

  const openEditForm = (pallet) => {
    if (tutorialModeRef?.current) return;
    setEditingPallet(pallet);
    setFormData({
      name: pallet.name, description: pallet.description || "", notes: pallet.notes || "",
      image: pallet.image || null, images: pallet.images || (pallet.image ? [pallet.image] : null),
      components: pallet.components.length > 0
        ? pallet.components.map(c => ({
            name: c.name, quantity: c.quantity || "", length: c.length || "", height: c.height || "",
            thickness: c.thickness || "", notched: c.notched, notch_length: c.notch_length || "",
            pcs_per_min: c.pcs_per_min || "", board_footage: c.board_footage || "", cost: c.cost || "",
            lumber: c.lumber?.length ? c.lumber.map(l => ({ length: l.length || "8", quantity: l.quantity || "", description: l.description || "", thickness: l.thickness || "3.5", width: l.width || "6" })) : emptyLumber(),
            skids: c.skids || "", extras: c.extras || "",
          }))
        : defaultComponents(),
    });
    setIsFormOpen(true);
  };

  const openCreateForm = () => {
    setEditingPallet(null); resetForm(); setIsFormOpen(true);
  };

  const addComponent = () => {
    setFormData(prev => prev.components.length >= 15 ? prev : ({ ...prev, components: [...prev.components, { name: "", quantity: "", length: "", height: "", thickness: "", notched: null, notch_length: "", pcs_per_min: "", board_footage: "", cost: "", lumber: emptyLumber(), skids: "", extras: "" }] }));
  };

  const removeComponent = (index) => {
    setFormData(prev => ({ ...prev, components: prev.components.filter((_, i) => i !== index) }));
  };

  const updateComponent = (index, field, value) => {
    setFormData(prev => {
      const newComponents = [...prev.components];
      newComponents[index] = { ...newComponents[index], [field]: value };
      return { ...prev, components: newComponents };
    });
  };

  const updateLumber = (compIdx, lumberIdx, field, value) => {
    setFormData(prev => {
      const newComponents = [...prev.components];
      const newLumber = [...(newComponents[compIdx].lumber || emptyLumber())];
      newLumber[lumberIdx] = { ...newLumber[lumberIdx], [field]: value };
      newComponents[compIdx] = { ...newComponents[compIdx], lumber: newLumber };
      return { ...prev, components: newComponents };
    });
  };

  const compressImage = (file) => new Promise((resolve) => {
    if (!file.type?.startsWith('image/') || file.type === 'image/gif') return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const maxDim = 1280;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (blob && blob.size < file.size) {
          const name = (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        } else resolve(file);
      }, 'image/jpeg', 0.8);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  const handleImageUpload = async (file) => {
    if (tutorialModeRef?.current || !file) return;
    let toUpload = file;
    try { toUpload = await compressImage(file); } catch {}
    if (toUpload.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    const uploadFormData = new FormData();
    uploadFormData.append('file', toUpload);
    try {
      const { data } = await axios.post(`${API}/images`, uploadFormData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 45000 });
      const imageUrl = data.url;
      setFormData(prev => {
        const existingImages = prev.images || [];
        const newImages = [...existingImages, imageUrl];
        return { ...prev, image: newImages[0], images: newImages };
      });
      toast.success("Photo added — remember to hit Save");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail ? `Upload failed: ${detail}` : (navigator.onLine ? "Failed to upload image" : "Can't upload photos while offline"));
    }
  };

  const handleImageRemove = (index) => setFormData(prev => {
    const existingImages = prev.images || [];
    const newImages = existingImages.filter((_, i) => i !== index);
    return { ...prev, image: newImages[0] || null, images: newImages.length > 0 ? newImages : null };
  });

  const handleManualSync = async (fetchSavedList) => {
    if (tutorialModeRef?.current) return;
    // Everything in parallel — one round-trip instead of four sequential ones
    await Promise.all([syncWithServer(), fetchSavedList()]);
  };

  return {
    palletTypes, setPalletTypes, selectedPalletType, setSelectedPalletType,
    isFormOpen, setIsFormOpen, editingPallet, deleteConfirmId, setDeleteConfirmId,
    loading, searchTerm, setSearchTerm, isOnline, pendingSync,
    formData, setFormData, fetchPalletTypes,
    handleCreateOrUpdate, handleDelete, openEditForm, openCreateForm,
    addComponent, removeComponent, updateComponent, updateLumber,
    handleImageUpload, handleImageRemove, handleManualSync, syncWithServer,
  };
}
